import { Avatar, Box, Grid, IconButton, Typography, styled } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LabeledInput from '../../Common/LabeledInput';
import StyledBackButton from '../../Common/StyledBackButton';
import StyledButton from '../../Common/StyledButton';
import { useAppSelector } from '../../../redux/hooks';
import uiConfig from '../../../config/uiConfig';
import { UserStatusType } from '../../../types/coach';

interface IClientArchiveModalProps {
  handleOnClick: (_password?: string) => void;
  handleDismiss: () => void;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture: string;
  title: string;
  subTitle: string;
  btnText: string;
  icon: string;
}

const StyledGrid = styled(Grid)({
  background: 'linear-gradient(0deg, rgba(124, 174, 190, 0.2), rgba(124, 174, 190, 0.2))',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '11px',
});

function ClientArchiveModal({
  handleOnClick,
  handleDismiss,
  firstName,
  lastName,
  email,
  profilePicture,
  title,
  subTitle,
  btnText,
  icon,
}: IClientArchiveModalProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // const [allValid, setAllValid] = useState(false);
  const archiveClientLoading = useAppSelector((state) => state.client.archiveClientLoading);
  const userStatusType = useAppSelector((state) => state.coach.coach?.userStatusType);

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };
  const handleDeleteClick = () => {
    handleOnClick(password);
  };

  const randomIndex = Math.floor(Math.random() * uiConfig.avatarBackgroundColors.length);
  const randomBackgroundColor = uiConfig.avatarBackgroundColors[randomIndex];

  return (
    <Grid container maxHeight={709} flexDirection="column">
      <StyledGrid item height={167}>
        <img src={icon} alt="bin" role="presentation" height={56} />
        <Typography variant="title2Bold" color="primary" textAlign="center">
          {title}
        </Typography>
      </StyledGrid>
      <Grid item maxHeight={542} px={4} pt={6.5} pb={4.5}>
        <Grid container spacing={5} flexDirection="column">
          <Grid item textAlign="center">
            <Typography variant="bodyRegular" color="primary" sx={{ letterSpacing: '-0.41px' }}>
              {subTitle}
            </Typography>
          </Grid>
          <Grid item display="flex" flexDirection="column" gap="16px">
            <Box display="flex" alignItems="center" justifyContent="center">
              {profilePicture ? (
                <img
                  src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${profilePicture}`}
                  alt="bin"
                  style={{
                    width: '72px',
                    height: '72px',
                    objectFit: 'cover',
                    borderRadius: '50%',
                  }}
                />
              ) : (
                <Avatar sx={{ width: '72px', height: '72px', bgcolor: randomBackgroundColor }}>
                  {`${firstName ? firstName[0].toUpperCase() : ''}${
                    lastName ? lastName[0].toUpperCase() : ''
                  }`}
                </Avatar>
              )}
            </Box>
            <Box display="flex" alignItems="center" flexDirection="column" gap="4px">
              <Typography variant="title3Regular" color="primary" textAlign="center">
                {`${firstName || ''} ${lastName || ''}`}
              </Typography>
              <Typography variant="bodyRegular" color="#001E1D" textAlign="right">
                {email}
              </Typography>
            </Box>
          </Grid>
          {userStatusType !== UserStatusType.EXTERNAL_PROVIDER && (
            <Grid item>
              <LabeledInput
                label={t('auth.password')}
                required
                name="newPassword"
                placeholder={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                id="new-password"
                value={password}
                handleChange={handleChange}
                startIcon={<img src="/assets/icons/security.svg" alt="email" />}
                endIcon={
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handlePasswordVisibility}
                  >
                    {showPassword ? (
                      <img src="/assets/icons/visibility.svg" alt="visibility" />
                    ) : (
                      <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                    )}
                  </IconButton>
                }
              />
            </Grid>
          )}
          <Grid item>
            <Grid container justifyContent="space-between">
              <StyledBackButton
                variant="outlined"
                onClick={handleDismiss}
                disabled={archiveClientLoading}
              >
                {t('common.dismiss')}
              </StyledBackButton>
              <StyledButton
                onClick={handleDeleteClick}
                disabled={userStatusType !== UserStatusType.EXTERNAL_PROVIDER && password === ''}
                loading={archiveClientLoading}
              >
                {btnText}
              </StyledButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default ClientArchiveModal;
